import React, { useState, Fragment } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { Tooltip } from 'antd';
import { get, join, map, size } from 'lodash';
import { withNavigation } from '../../../../../hoc';
import {
    Title,
    Text,
    Row,
    Column,
    Divider,
    AppIcon,
    FlexContainer,
    Icon,
    ConditionalRender,
    Truncate,
    OrderShippingEstimate
} from '../../../../../components/common';
import OrderAuditManagement, { OrderAuditManagementConsumer } from '../../../../../components/firebase/OrderAuditManagement';
import OrderEventsFeed from '../../../../../components/firebase/OrderEventsFeed';
import OrderStatusAudit from '../OrderStatusAudit';
import { timeDiffIn, formatTime, cleanOrderId, capitalizeNames } from '../../../../../utils/misc';
import useInterval from '../../../../../components/hooks/useInterval';
import { RED } from '../../../../../utils/colors';

const OrderBodyRow = styled(Row)`
    min-height: 30px;
`;

const CardText = styled(Text)`
    font-size: ${({ size = 12 }) => size}px;
    color: ${({ color = '#333333' }) => color};
    text-align: center;
`;

const CardTitle = styled(Title)`
    font-size: ${({ size = 12 }) => size}px;
    color: ${({ color = '#333333' }) => color};
    text-align: right;
`;

const OrderBody = ({ data, text, createdAt }) => {
    const [minutes, updateMinutes] = useState(0);

    useInterval(() => {
        updateMinutes(minutes + 1);
    }, 60000);

    return (
        <OrderStatusAudit orderId={get(data, 'id')}>
            {({ ts }) => {
                const timeInAudit = timeDiffIn(ts, 'minutes', moment(createdAt).add(minutes, 'minutes'));

                return (<>
                    <OrderBodyRow>
                        <Column align='center' style={{ textAlign: 'center' }}>
                            <Text text={text} strong size={11} />
                            <CardText size={11} text='Hora entrega' />
                        </Column>
                        <Column align='center' justify='center'>
                            <Text text={formatTime(timeInAudit)} size={11} color={timeInAudit >= 5 ? '#db5959' : '#4cb358'} strong />
                            <CardText size={11} text='En auditoría' />
                        </Column>
                    </OrderBodyRow>
                    <Divider />
                    <OrderBodyRow>
                        <Column align='flex-start' justify='center'>
                            <CardText text='Productos aprobados' />
                        </Column>
                        <Column align='flex-end' justify='center' flex={0.2}>
                            <CardTitle>{get(data, 'assignInfo.auditInfo.passedCount')}</CardTitle>
                        </Column>
                    </OrderBodyRow>
                    <Divider />
                    <OrderBodyRow>
                        <Column align='flex-start' justify='center'>
                            <CardText text='Productos en validación' />
                        </Column>
                        <Column align='flex-end' justify='center' flex={0.2}>
                            <CardTitle>{get(data, 'assignInfo.auditInfo.failedCount')}</CardTitle>
                        </Column>
                    </OrderBodyRow>
                    <Divider />
                    <OrderBodyRow>
                        <Column align='flex-start' justify='center'>
                            <CardText text='Regla auditoría' />
                        </Column>
                        <Column align='flex-end' justify='center' flex={1.5}>
                            <CardTitle>
                                {map(get(data, 'assignInfo.auditInfo.rules', []), (rule, index) => (
                                    <Fragment key={index}>
                                        <Text text={`order_audit.rule.${rule}`} strong />
                                        {(index + 1 < (size(get(data, 'assignInfo.auditInfo.rules', [])))) && <span> - </span>}
                                    </Fragment>
                                ))}
                            </CardTitle>
                        </Column>
                    </OrderBodyRow>
                    <Divider />
                    <OrderBodyRow>
                        <Column align='flex-start' justify='center'>
                            <CardText text='Shopper' />
                        </Column>
                        <Column align='flex-end' justify='center' flex={1.5}>
                            <Text
                                strong
                                align='right'
                                text={<Truncate width={150}>
                                    {capitalizeNames(join([get(data, 'assignInfo.picker.firstName'), get(data, 'assignInfo.picker.lastName')], ' '))}
                                </Truncate>}
                            />
                        </Column>
                    </OrderBodyRow>
                    <Divider />
                </>);
            }}
        </OrderStatusAudit>
    );
};

const renderAuditStatus = (orderId) => (
    <OrderAuditManagementConsumer orderId={orderId}>
        {({ taken, userName, loading }) => (
            <ConditionalRender condition={taken && !loading}>
                <FlexContainer justify='flex-end'>
                    <Tooltip placement='top' title={`Tomada por ${userName}`}>
                        <AppIcon name='closed-hand' size={20} color='#f39c12' />
                    </Tooltip>
                </FlexContainer>
            </ConditionalRender>
        )}
    </OrderAuditManagementConsumer>
);

const AuditOrderCardContentLegacy = ({ data, onClickHandler, bipickingInfo }) => {
    const orderId = get(data, 'id');
    const isExpress = get(data, 'logisticsInfo.selectedSla', null) === 'Despacho Express';

    return (
        <OrderEventsFeed orderId={orderId}>
            <OrderAuditManagement orderId={orderId}>
                <OrderShippingEstimate {...data}>
                    {({ diffInMinutes, deliveryDate, deliveryTime, deliveryTimeHour }) => {
                        return (<div style={{ display: 'flex', flexDirection: 'column', flex: 1 }} onClick={() => onClickHandler(data)}>
                            <Row>
                                <Row gap={5} flex={4} align='center'>
                                    {bipickingInfo?.isBipicking && (
                                        <Icon 
                                            name={bipickingInfo.iconName}
                                            size={16} 
                                            color={bipickingInfo.iconColor}
                                        />
                                    )}
                                    <a href={`/orders/${orderId}`}>
                                        <Text
                                            text={<>
                                                { cleanOrderId(orderId) }
                                                { get(data, 'customer.isVIP') && <Icon name='vip' size={20} left={5} color='#4DB457' /> }
                                            </>}
                                            size={14}
                                            strong
                                            color='#4cb358'
                                        />
                                    </a>
                                </Row>
                                <Row gap={5} flex={5} align='center'>
                                    {bipickingInfo?.showRelatedLabel && (<>
                                        <Icon 
                                            name='bipicking-b'
                                            size={16} 
                                            color={RED}
                                        />
                                        <a href={`/orders/${bipickingInfo.partnerId}`}>
                                            <Text
                                                text='Order relacionada'                                                                        size={12}
                                                style={{ textDecoration: 'underline' }}
                                            />
                                        </a>
                                    </>)}
                                </Row>
                                <Row flex={1}>
                                    {renderAuditStatus(orderId)}
                                </Row>
                            </Row>
                            <Divider />
                            <Row justify='space-between' align='center'>
                                <Text text='Fecha de entrega' />
                                <Text text={deliveryDate} />
                            </Row>
                            {diffInMinutes < -45 && <Divider bottom='-15' />}
                            <OrderBody data={data} text={isExpress ? deliveryTimeHour : deliveryTime} />
                        </div>);
                    }}
                </OrderShippingEstimate>
            </OrderAuditManagement>
        </OrderEventsFeed>
    );
};

export default withNavigation(AuditOrderCardContentLegacy);